import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, timeout, map, switchMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

import * as _ from 'lodash';

import { environment } from '../../environments/environment';
import { AppService } from 'src/app/app.service';

export const ACTION_TYPES = {
    PAYMENT_LINK: 'PAYMENT_LINK',
    PAYMENT_SUBMIT: 'PAYMENT_SUBMIT',
};

@Injectable({
	providedIn: 'root'
})
export class AuthService {

    appConfig = environment.appConfig;
	appHttpOptions = {
		headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': '' }),
	};

	loyaltyHttpOptions = {
		headers: new HttpHeaders({
			'Content-Type': 'application/json',
			'env': this.appConfig.loyaltyEnv,
			'JoinChannelGuid': this.appConfig.loyaltyJoinChannelGUID,
			'siteId': this.appConfig.tabitDefaultOrganizationId,
			//'siteId': this.appService.getOrganization() || this.appConfig.tabitDefaultOrganizationId,
			'client_id': this.appConfig.tabitClientID // ROS Client ID
		})
	};

	public signinChangedSource = new Subject<void>();
	public signinChanged$ = this.signinChangedSource.asObservable();

    restorePromise: any;

	constructor(
		private http: HttpClient,
		private router: Router,
		private appService: AppService,
	) {	}

	public isAuthenticated(): boolean {
		const token = localStorage.getItem('loyaltyToken');
		return token != null && token != '';
	}

	public setHttpHeadersAccessToken(token: string, _anonimus?: boolean, dontNotify?: boolean): void {
		let anonimus = !!_anonimus

		this.appHttpOptions.headers = this.appHttpOptions.headers.set('Authorization', `Bearer ${token}`);

		if (anonimus) {
			sessionStorage.setItem('token', token);
			this.appService.user = null;
		} else {
			localStorage.setItem('token', token);
		}
		if (!dontNotify) this.signinChangedSource.next();
	}

	public setHttpHeadersLoyaltyAccessToken(token: string, refreshToken: string): void {
		this.loyaltyHttpOptions.headers = this.loyaltyHttpOptions.headers.set('Authorization', `Bearer ${token}`);
		localStorage.setItem('loyaltyToken', token);
		localStorage.setItem('loyaltyRefreshToken', refreshToken);
	}

	public restoreSignIn(credentials?) {
		const token = localStorage.getItem('token');
		const loyaltyToken = localStorage.getItem('loyaltyToken');

        // Try get the tokens from the params
		if (token && loyaltyToken) {
			this.setHttpHeadersLoyaltyAccessToken(loyaltyToken, localStorage.getItem('loyaltyRefreshToken'));
			this.setHttpHeadersAccessToken(token);
			return this.getUserInfo(true)
            .catch(err => {
				return this.signInAnonimus();
			});
		} else {
			return this.signInAnonimus();
		}
	}

	public refreshSignin(isLoyaly?) {
		if (this.restorePromise) return this.restorePromise;
		const refresh_token = localStorage.getItem('loyaltyRefreshToken') || this.appService.urlParams?.refreshToken;
		if (refresh_token) {
			this.restorePromise = this.refreshLoyaltySignIn()
            .then(response => {
				this.setHttpHeadersLoyaltyAccessToken(response.ResponseData.Access_token, response.ResponseData.Refresh_token);
				return this.get(`${this.appConfig.tabitLoyaltyAPI}/auth/rostoken`, this.loyaltyHttpOptions)
                .then(res => {
					localStorage.setItem('refresh_token', res.ResponseData.Refresh_token);
					this.setHttpHeadersAccessToken(res.ResponseData.Access_token);
					return {success:true}
				}).catch(err => {
					throw (err);
				}).then(o => {
					this.restorePromise = null;
				})
			}).catch(err => {
				this.signInAnonimus();
				throw (err);
			});

			return this.restorePromise;
		}
		return Promise.reject();
	}

	public refreshLoyaltySignIn() {
		const request = {
			accessToken: localStorage.getItem('loyaltyToken'),
			refreshToken: localStorage.getItem('loyaltyRefreshToken')
		}
		return this.post(`${this.appConfig.tabitLoyaltyAPI}/auth/renew`, request, this.loyaltyHttpOptions);
	}

	public refreshRosSignIn(refreshToken) {
		const request = {
			client_id: 'urfoxj8ymmz2bbfolofq4xsjr9psc3q7ii76b2n3',
			grant_type: 'refresh_token',
			refresh_token: refreshToken,
		}
		return this.post('/oauth2/token', request);
	}

	public signInAnonimus() {
		localStorage.removeItem('loyaltyToken');
		localStorage.removeItem('loyaltyRefreshToken');
		localStorage.removeItem('token');
		localStorage.removeItem('refresh_token');

		const request = {
			client_id: 'urfoxj8ymmz2bbfolofq4xsjr9psc3q7ii76b2n3',
			grant_type: 'client_credentials',
			scope: 'online-account',
		}
		return this.post('/oauth2/token', request)
        .then(response => {
			this.setHttpHeadersAccessToken(response.access_token, true);
		});
	}

	//-------------------------------------------------------------------------------->
	// loyalty
	//-------------------------------------------------------------------------------->

	public generateVerificationCodeMessage(): String {
		return this.appService.translate('verification_code_message');
	}

	public signInByPhone$(args: any): Observable<any> {
		return this.http.post<{}>(`${this.appConfig.tabitLoyaltyAPI}/auth/mobile`, {
			mobile: args.phone,
			customMessage: this.generateVerificationCodeMessage(),
		}, this.loyaltyHttpOptions)
			.pipe(
				timeout(15000),
			);
	}

	public resendPicode$(args: any): Observable<any> {
		return this.http.post<{}>(`${this.appConfig.tabitLoyaltyAPI}/customer/resenddotp`, {
			mobile: args.phone,
			customMessage: this.generateVerificationCodeMessage(),
		}, this.loyaltyHttpOptions)
			.pipe(
				timeout(15000),
			);
	}

	public signInByPhoneVerifyCode$(args: any): Observable<any> {
		return this.http.post<{}>(`${this.appConfig.tabitLoyaltyAPI}/auth/mobile/pincode`, { mobile: args.phone, pinCode: args.code }, this.loyaltyHttpOptions)
			.pipe(
				timeout(15000),
			)
	}

	public insertCustomer$(args: any): Observable<any> {
		return this.http.post<{}>(`${this.appConfig.tabitLoyaltyAPI}/customer/createandsendotp`, {
			Mobile: args.phone,
			FirstName: args.FirstName,
			LastName: args.LastName,
			Email: args.Email,
			customMessage: this.generateVerificationCodeMessage(),
		}, this.loyaltyHttpOptions)
			.pipe(
				timeout(15000),
			)
	}

	public updateCustomer$(args: any): Observable<any> {
		return this.http.put<{}>(`${this.appConfig.tabitLoyaltyAPI}/customer`, args, this.loyaltyHttpOptions)
			.pipe(
				timeout(15000),
				map((loyaltyResponse: any) => {
					_.assignIn(this.appService.lUser, loyaltyResponse.ResponseData);
					this.prepareCustomer(this.appService.lUser);
					return loyaltyResponse;
				})
			)
	}

	public getROSToken$(args: any): Observable<any> {
		return this.http.get(`${this.appConfig.tabitLoyaltyAPI}/auth/rostoken`, this.loyaltyHttpOptions)
			.pipe(
				timeout(15000),
			);
	}

	public signInByEmail$(args) {
		return this.http.post<{}>(`${this.appConfig.tabitLoyaltyAPI}/auth/email`, {
			Mobile: args.phone,
			UserName: args.email,
			Password: args.password,
		}, this.loyaltyHttpOptions)
			.pipe(
				timeout(15000),
			);
	}

	public checkGiftCard(pm): Promise<any> {
		let headers: any = new HttpHeaders({
			'Content-Type': 'application/json',
			'env': this.appConfig.loyaltyEnv,
			'accountGuid': pm.loyaltyApiKey,
			'JoinChannelGuid': this.appConfig.loyaltyJoinChannelGUID,
			'siteId': this.appConfig.tabitDefaultOrganizationId,
			'client_id': this.appConfig.tabitClientID // ROS Client ID
		})
		return new Promise((resolve, reject) => {
			this.http.post(`${this.appConfig.tabitLoyaltyAPI}/auth/rostoken`, {cardNum: pm.cardNum, cvv: pm.cvv}, headers)
				.subscribe(
					(results: any) => {
						resolve(results);
					},
					(err) => {
						this.appService.sendAnalyticsEvent('exception', { description: err.message });
						reject(err);
					}
				);
		});
	}

	//-------------------------------------------------------------------------------->

	public prepareCustomer(customer) {
		let fn = customer.FirstName;
		let ln = customer.LastName;

		let arr = [];
		if (fn) arr.push(fn);
		if (ln && ln.length > 1) arr.push(ln);
		customer.FullName = arr.join(" ");

		this.appService.contact = {
			firstName: customer.FirstName,
			lastName: customer.LastName,
			name: customer.FullName,
			email: customer.Email,
			phone: customer.Mobile
		}
	}

	public prepareUser(_user?, loyaltyUser?) {
		let initials = '?';

		if (loyaltyUser?.FirstName && loyaltyUser.FirstName.length) {
			initials = loyaltyUser?.FirstName.substr(0, 1);
			if (loyaltyUser.LastName && loyaltyUser.LastName.length) {
				initials += loyaltyUser?.LastName.substr(0, 1);
			}
		}
		let user: any = _.assignIn({
			wallet: null,
			name: loyaltyUser?.FullName,
			initials: initials
		}, _user);

		return user;
	}

	//-------------------------------------------------------------------------------->
	// user info
	//-------------------------------------------------------------------------------->

	public getUserInfo(withWallet?) {
		return this.callSignedHTTP('get', `${this.appConfig.tabitLoyaltyAPI}/customer`)
		.then(loyaltyUser => {
			const promises = [
				this.callSignedHTTP('get', '/online-shopper/customers/current'),
			];
			if (this.appService.regionArgs.enableWallet) {
				promises.push(this.callSignedHTTP('get', '/online-shopper/customers/current/wallet'))
			}
			return Promise.all(promises)
			.then(response => {
				const lUser = this.appService.lUser = loyaltyUser.ResponseData || {};
				if (lUser.LastName && lUser.LastName.length < 2) delete lUser.LastName;
				this.prepareCustomer(this.appService.lUser);

				let user = this.appService.user = this.prepareUser(response[0], lUser);
				if (user.name) localStorage.setItem('c_name', user.name);
				if (user.phone) localStorage.setItem('c_phone', _.get(user, 'phones[0].pstn', user.phone));

				// Wallet data
				if (response[1]) {
					const wallet = response[1];
					if (wallet.payments?.length) wallet.payments = this.filterObsoletePayments(wallet.payments);
					this.appService.user.wallet = wallet;
				}
				return this.getUserMeta();

			});
		}).catch(err => {
			console.error('getUserInfo error', err);
			throw (err);
		})
	}


	public getUserMeta() {
		const user = this.appService.user;
		const userPayments = _.get(this.appService, 'user.wallet.payments', []);
		if (!userPayments.length) return Promise.resolve(user);

		return user;
	}

	public getUserWallet() {
		return this.callSignedHTTP('get', '/online-shopper/customers/current/wallet')
        .then(wallet => {
			if (wallet.payments?.length) wallet.payments = this.filterObsoletePayments(wallet.payments);
			this.appService.user.wallet = wallet;

			return this.getUserMeta();
		});
	}

	public callSignedHTTP(method, url, payload = {}, headers?, signed = true, firstTry = true, enableRecaptcha = false) {
		let promise: any;
		let that = this;

		let args: any = {
			method: method,
			url: url,
			payload: payload,
			headers: headers,
			signed: signed,
			firstTry: firstTry,
			isLoyalty: url.indexOf(this.appConfig.tabitLoyaltyAPI) != -1
		}

		let baseHeaders = args.isLoyalty ? this.loyaltyHttpOptions : this.appHttpOptions;

		let params: any = _.assignIn({}, baseHeaders);
		if (headers) {
			_.each(params.headers.keys(), key => {
				headers[key] = params.headers.get(key);
			});
			params.headers = headers;
		}

		switch (method) {
			case 'post': promise = this.post(url, payload, params, enableRecaptcha); break;
			case 'put': promise = this.put(url, payload, params); break;
			case 'patch': promise = this.patch(url, payload, params); break;
			case 'get':
				params.params = payload;
				promise = this.get(url, params);
				break;
			case 'remove':
				params.params = payload;
				promise = this.remove(url, params);
				break;
			case 'delete':
				params.body = payload;
				promise = this.remove(url, params);
				break;
		}
		return promise.catch(error => {
			if (error.status == 401) {
				if (args.firstTry) {
					return that.refreshSignin(args.isLoyalty)
                    .then(() => {
						return that.callSignedHTTP(args.method, args.url, args.headers ,args.payload, args.signed, false);
					});
				} else {
					document.location.reload();
					throw (error);
				}
			} else {
				throw (that.handleHttpError(error));
			}
		})
	}

	//-------------------------------------------------------------------------------->
	// old sign in methods
	//-------------------------------------------------------------------------------->

	public forgotPassword(email) {
		let that = this;
		return this.callSignedHTTP('remove', '/online-shopper/customers/' + email + '/password', { local: this.appService.locale }, { 'ros-organization': this.appService.urlParams.site }).then(response => {
			return response;
		}).catch(e => {
			console.error(e);
			throw ({ type: 'ERROR', message: "SERVER_MESSAGES.EMAIL_NOT_FOUND" });
		})
	}

	public signOut(confirm?): Promise<any> {
		let that = this;
		if (confirm) {
			return this.appService.confirm({ content: 'MESSAGES.CONFIRM_SIGNOUT' }).then((res) => {
				return this.signOut();
			}).catch(err => { });
		}

		return this.callSignedHTTP('remove', "/oauth2/token").then(response => {
			localStorage.removeItem('token');
			localStorage.removeItem('refresh_token');
			that.signInAnonimus();
			//that.appService.toastr('MESSAGES.SIGNOUT_SUCCESS', { type: 'success' });
		}).catch(function (e) {
			console.error('remove remote token failed', e)
		});
	};


	//-------------------------------------------------------------------------------->
	// http methods
	//-------------------------------------------------------------------------------->

	public handleHttpError(error: any) {
		console.error(error); // log to console instead
		return error
	}

	public post(url, payload?, headers?, enableRecaptcha?: boolean): Promise<any> {
		if (url.indexOf('http') == -1) url = this.appConfig.tabitAPI + url;
		enableRecaptcha
		return new Promise((resolve, reject) => {
			(!enableRecaptcha ? this.http.post(url, payload || {}, headers || {}) : this.processRequestWithCaptcha(ACTION_TYPES.PAYMENT_SUBMIT, url, payload, headers))
				.subscribe(
					(results: any) => {
						resolve(results);
					},
					(err) => {
						this.appService.sendAnalyticsEvent('exception', { description: err.message });
						reject(err);
					}
				);
		});
	}

	public put(url, payload?, headers?): Promise<any> {
		if (url.indexOf('http') == -1) url = this.appConfig.tabitAPI + url;
		return new Promise((resolve, reject) => {
			this.http.put(url, payload || {}, headers || {})
				.subscribe(
					(results: any) => {
						resolve(results);
					},
					(err) => {
						this.appService.sendAnalyticsEvent('exception', { description: err.message });
						reject(err);
					}
				);
		});
	}

	public patch(url, payload?, headers?): Promise<any> {
		if (url.indexOf('http') == -1) url = this.appConfig.tabitAPI + url;
		return new Promise((resolve, reject) => {
			this.http.patch(url, payload || {}, headers || {})
				.subscribe(
					(results: any) => {
						resolve(results);
					},
					(err) => {
						this.appService.sendAnalyticsEvent('exception', { description: err.message });
						reject(err);
					}
				);
		});
	}

	public get(url, payload?): Promise<any> {
		if (url.indexOf('http') == -1) url = this.appConfig.tabitAPI + url;
		return new Promise((resolve, reject) => {
			this.http.get(url, payload || this.appHttpOptions)
				.subscribe(
					(results: any) => {
						resolve(results);
					},
					(err) => {
						this.appService.sendAnalyticsEvent('exception', { description: err.message });
						reject(err)
					}
				);
		});
	}

	public remove(url, payload?, headers?): Promise<any> {
		return new Promise((resolve, reject) => {
			if (url.indexOf('http') == -1) url = this.appConfig.tabitAPI + url;
			this.http.delete(url, payload || this.appHttpOptions)
				.subscribe(
					(results: any) => {
						resolve(results);
					},
					(err) => {
						this.appService.sendAnalyticsEvent('exception', { description: err.message });
						reject(err);
					}
				);
		});
	}

    // reCAPTCHA enterprise integration
    // Create a single function handler to combine the two operations
    processRequestWithCaptcha(action: string, url: string, reqBody: any, overrideHeaders?: any): Observable<any> {
        // Get the reCaptcha token asynchronously and use it for the HTTP POST request
        return this.getReCaptchaToken(action).pipe(
            switchMap(token => {
                reqBody.recaptchaToken = token;
                // Make the HTTP POST request with the updated headers and requestBody
                return this.http.post(url, reqBody, overrideHeaders || this.appHttpOptions);
            }),
            catchError(error => {
                // Handle the error during token creation (e.g., log, notify, etc.)
                console.error('Error during Token creation:', error);

                // Rethrow the error to propagate it to the subscriber
                throw error;
            })
        );
    }

    getReCaptchaToken(action): Observable<any> {
        const recaptchaSiteKey = this.appConfig?.recaptchaSiteKey;

        return new Observable(observer => {
            if (!recaptchaSiteKey || !window['grecaptcha']) {
                this.appService.logTemplate("reCAPTCHA error", {}, "reCAPTCHA error - Missing main configuration");
                observer.error(new Error('Missing main configuration'));
            }
            window['grecaptcha'].enterprise.ready(() => {
                window['grecaptcha'].enterprise.execute(recaptchaSiteKey, { action })
                .then(token => {
                    if (!token) {
                        this.appService.logTemplate("reCAPTCHA error", {}, "Token not generated");
                        observer.error('Token generation error');
                    }
                    observer.next(token);
                    observer.complete();
                },error => {
                    this.appService.logTemplate("reCAPTCHA error", {}, JSON.stringify(error));
                    observer.error(error);
                });
            });
        })
    };

	private filterObsoletePayments(payments) {
		const filteredPayments = payments?.filter(walletPayment => {
			if (walletPayment.paymentType == 'creditCard' && !walletPayment.hasProviderToken) return false;
			else return true;
		}); // Filter out payments with the 'hasProviderToken' property

		return filteredPayments;
	}

}
